(() => {
  const STORAGE_KEY = 'goldenLeafRecentlyViewedProducts';
  const MAX_RECENTLY_VIEWED_STORED = 20;
  const RESULTS_SECTION_ID = 'fancy-product-carousel-results';

  function getShopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return '/';
  }

  function normalizeRoot(root) {
    if (!root) return '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function parseJsonScript(section) {
    const script = section.querySelector('[data-fancy-product-carousel-config]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Fancy product carousel: invalid config JSON.', error);
      return null;
    }
  }

  function normalizeArray(value) {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item && typeof item === 'object' && 'value' in item) return item.value;
          return item;
        })
        .filter(Boolean)
        .map(String);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [String(value)];
  }

  function getRecentlyViewed() {
    try {
      const rawValue = window.localStorage.getItem(STORAGE_KEY);
      if (!rawValue) return [];

      const parsedValue = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) return [];

      return parsedValue
        .map((item) => {
          if (typeof item === 'string') {
            return {
              handle: item,
              url: `${normalizeRoot(getShopifyRoot())}products/${item}`
            };
          }

          if (item && item.handle) {
            return {
              handle: String(item.handle),
              url: item.url || `${normalizeRoot(getShopifyRoot())}products/${item.handle}`
            };
          }

          return null;
        })
        .filter(Boolean);
    } catch (error) {
      console.warn('Fancy product carousel: could not read recently viewed products.', error);
      return [];
    }
  }

  function saveRecentlyViewed(items) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items.slice(0, MAX_RECENTLY_VIEWED_STORED))
      );
    } catch (error) {
      console.warn('Fancy product carousel: could not save recently viewed products.', error);
    }
  }

  function recordCurrentProduct(config) {
    if (!config || !config.currentProduct || !config.currentProduct.handle) return;

    const currentProduct = {
      handle: config.currentProduct.handle,
      url: config.currentProduct.url || `${normalizeRoot(getShopifyRoot())}products/${config.currentProduct.handle}`
    };

    const existingItems = getRecentlyViewed();
    const nextItems = [
      currentProduct,
      ...existingItems.filter((item) => item.handle !== currentProduct.handle)
    ];

    saveRecentlyViewed(nextItems);
  }

  function createBaseResultsUrl(config) {
    const baseUrl = config.allProductsUrl || '/collections/all';
    const url = new URL(baseUrl, window.location.origin);

    url.searchParams.set('section_id', RESULTS_SECTION_ID);
    url.searchParams.set('filter.v.availability', '1');

    if (config.sortBy && config.sortBy !== 'manual') {
      url.searchParams.set('sort_by', config.sortBy);
    }

    return url;
  }

  function buildFilteredResultsUrl(config) {
    const url = createBaseResultsUrl(config);

    if (config.source === 'same_author') {
      const values = normalizeArray(config.authorMetafield && config.authorMetafield.values);
      const namespace = config.authorMetafield && config.authorMetafield.namespace;
      const key = config.authorMetafield && config.authorMetafield.key;

      if (!namespace || !key || values.length === 0) return null;

      values.forEach((value) => {
        url.searchParams.append(`filter.p.m.${namespace}.${key}`, value);
      });

      return url;
    }

    if (config.source === 'same_genres') {
      const values = normalizeArray(config.genresMetafield && config.genresMetafield.values);
      const namespace = config.genresMetafield && config.genresMetafield.namespace;
      const key = config.genresMetafield && config.genresMetafield.key;

      if (!namespace || !key || values.length === 0) return null;

      values.forEach((value) => {
        url.searchParams.append(`filter.p.m.${namespace}.${key}`, value);
      });

      return url;
    }

    if (config.source === 'same_category') {
      const categoryId = config.category && config.category.id;
      if (!categoryId) return null;

      url.searchParams.set('filter.p.t.category', categoryId);
      return url;
    }

    return null;
  }

  function buildSingleMetafieldValueUrls(config, metafieldConfig) {
    const namespace = metafieldConfig && metafieldConfig.namespace;
    const key = metafieldConfig && metafieldConfig.key;
    const values = normalizeArray(metafieldConfig && metafieldConfig.values);

    if (!namespace || !key || values.length === 0) return [];

    return values.map((value) => {
      const url = createBaseResultsUrl(config);
      url.searchParams.set(`filter.p.m.${namespace}.${key}`, value);
      return url;
    });
  }

  function buildSingleGenreResultsUrls(config) {
    return buildSingleMetafieldValueUrls(config, config.genresMetafield);
  }

  function buildSingleAuthorResultsUrls(config) {
    return buildSingleMetafieldValueUrls(config, config.authorMetafield);
  }

  function buildViewMoreUrl(resultsUrl) {
    const url = new URL(resultsUrl.toString());
    url.searchParams.delete('section_id');
    return url.toString();
  }

  async function fetchHtml(url) {
    const response = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.text();
  }

  function parseResultItems(html) {
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');

    return Array.from(
      documentFragment.querySelectorAll('[data-fancy-product-carousel-result-item]')
    );
  }

  function dedupeResultItems(items) {
    const seen = new Set();

    return items.filter((item) => {
      const key = item.dataset.productHandle || item.dataset.productId || item.dataset.productUrl;

      if (!key) return true;
      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
  }

  function filterOutCurrentProduct(items, config) {
    const currentProductId = config.currentProduct && config.currentProduct.id
      ? String(config.currentProduct.id)
      : null;
    const currentProductHandle = config.currentProduct && config.currentProduct.handle
      ? String(config.currentProduct.handle)
      : null;

    return items.filter((item) => {
      const itemId = item.dataset.productId;
      const itemHandle = item.dataset.productHandle;

      if (currentProductId && itemId === currentProductId) return false;
      if (currentProductHandle && itemHandle === currentProductHandle) return false;

      return true;
    });
  }

  function buildProductUrlFromHandle(handle) {
    const root = normalizeRoot(getShopifyRoot());
    return new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
  }

  async function fetchRecentlyViewedItems(config) {
    const recentlyViewed = getRecentlyViewed();
    const currentHandle = config.currentProduct && config.currentProduct.handle;
    const productsToShow = Number(config.productsToShow || 8);
    const candidateLimit = Math.min(productsToShow + 4, MAX_RECENTLY_VIEWED_STORED);

    const handles = recentlyViewed
      .map((item) => item.handle)
      .filter(Boolean)
      .filter((handle, index, allHandles) => allHandles.indexOf(handle) === index)
      .filter((handle) => handle !== currentHandle)
      .slice(0, candidateLimit);

    const results = await Promise.allSettled(
      handles.map(async (handle) => {
        const url = buildProductUrlFromHandle(handle);
        url.searchParams.set('section_id', RESULTS_SECTION_ID);

        const html = await fetchHtml(url);
        const resultItems = parseResultItems(html);

        return resultItems.length > 0 ? resultItems[0] : null;
      })
    );

    const items = [];

    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      if (!result.value) return;

      items.push(result.value);
    });

    return {
      items: items.slice(0, productsToShow),
      viewMoreUrl: null,
      hasMoreResults: false
    };
  }

  async function fetchItemsFromUrls(urls) {
    const results = await Promise.allSettled(
      urls.map(async (url) => {
        const html = await fetchHtml(url);
        return parseResultItems(html);
      })
    );

    let items = [];

    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      items = items.concat(result.value);
    });

    return dedupeResultItems(items);
  }

  async function fetchFilteredItems(config) {
    let parsedItems = [];
    let viewMoreUrl = null;

    if (config.source === 'same_genres') {
      const urls = buildSingleGenreResultsUrls(config);

      if (urls.length === 0) {
        return {
          items: [],
          viewMoreUrl: null,
          hasMoreResults: false
        };
      }

      parsedItems = await fetchItemsFromUrls(urls);
      viewMoreUrl = urls.length > 0 ? buildViewMoreUrl(urls[0]) : null;
    } else if (config.source === 'same_author') {
      const urls = buildSingleAuthorResultsUrls(config);

      if (urls.length === 0) {
        return {
          items: [],
          viewMoreUrl: null,
          hasMoreResults: false
        };
      }

      parsedItems = await fetchItemsFromUrls(urls);
      viewMoreUrl = urls.length > 0 ? buildViewMoreUrl(urls[0]) : null;
    } else {
      const url = buildFilteredResultsUrl(config);

      if (!url) {
        return {
          items: [],
          viewMoreUrl: null,
          hasMoreResults: false
        };
      }

      const html = await fetchHtml(url);
      parsedItems = parseResultItems(html);
      viewMoreUrl = buildViewMoreUrl(url);
    }

    const filteredItems = filterOutCurrentProduct(parsedItems, config);
    const productsToShow = Number(config.productsToShow || 8);

    return {
      items: filteredItems,
      viewMoreUrl,
      hasMoreResults: filteredItems.length > productsToShow
    };
  }

  function patchProductCard(card, config) {
    if (!card || !config || !config.cardSettings) return;

    const cardSettings = config.cardSettings;
    const productCard = card.querySelector('.product-card');
    const imageLink = card.querySelector('.product-card__image-link');
    const title = card.querySelector('.product-card__title');
    const alternateFormats = card.querySelector('.book-buy-buttons__alternate-formats');
    const condition = card.querySelector('[data-product-condition]');
    const saleLabel = card.querySelector('[data-product-sale-label]');
    const priceBadges = card.querySelector('[data-product-price-badges]');

    if (productCard) {
      productCard.classList.toggle('product-card--arch', cardSettings.imageShape === 'arch');
    }

    if (imageLink) {
      Array.from(imageLink.classList).forEach((className) => {
        if (className.startsWith('product-card__image-link--')) {
          imageLink.classList.remove(className);
        }
      });

      imageLink.classList.add(`product-card__image-link--${cardSettings.imageRatio || 'portrait'}`);
      imageLink.classList.toggle('is-arch', cardSettings.imageShape === 'arch');
    }

    if (title) {
      title.classList.toggle('is-clamped', Boolean(cardSettings.enableTitleClamp));
      title.style.setProperty('--title-lines', cardSettings.titleLineClamp || 2);
    }

    if (alternateFormats) {
      alternateFormats.hidden = !cardSettings.showAltformatButtons;
    }

    if (condition) {
      condition.hidden = !cardSettings.showProductCondition;
    }

    if (saleLabel) {
      saleLabel.hidden = saleLabel.hidden || !cardSettings.showSaleBadge;
    }

    if (priceBadges && !cardSettings.showProductCondition && !cardSettings.showSaleBadge) {
      priceBadges.hidden = true;
    }
  }

  function createCarouselItem(resultItem, config) {
    const item = document.createElement('div');
    item.className = 'featured-collection__item';
    item.dataset.fancyProductCarouselItem = '';

    item.innerHTML = resultItem.innerHTML;

    const productId = resultItem.dataset.productId;
    const productHandle = resultItem.dataset.productHandle;
    const productUrl = resultItem.dataset.productUrl;

    if (productId) item.dataset.productId = productId;
    if (productHandle) item.dataset.productHandle = productHandle;
    if (productUrl) item.dataset.productUrl = productUrl;

    patchProductCard(item, config);

    return item;
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function getDefaultViewMoreText(config) {
    if (!config) return 'View more';

    if (config.viewMore && config.viewMore.text) {
      return config.viewMore.text;
    }

    if (config.source === 'same_author') return 'View more by this author';
    if (config.source === 'same_genres') return 'View more in this genre';

    if (config.source === 'same_category') {
      const categoryName = config.category && config.category.name;
      return categoryName ? `View more in ${categoryName}` : 'View more like this';
    }

    return 'View more';
  }

  function createViewMoreCard(config, url) {
    const item = document.createElement('div');
    item.className = 'featured-collection__item featured-collection__item--view-more';
    item.dataset.fancyProductCarouselViewMore = '';

    const imageRatio = config.cardSettings && config.cardSettings.imageRatio
      ? config.cardSettings.imageRatio
      : 'portrait';
    const isArch = config.cardSettings && config.cardSettings.imageShape === 'arch';
    const text = getDefaultViewMoreText(config);
    const safeText = escapeHtml(text);
    const safeUrl = escapeHtml(url);

    item.innerHTML = `
      <article class="product-card carousel-view-more-card fancy-product-carousel__view-more-card${isArch ? ' product-card--arch' : ''}">
        <a
          href="${safeUrl}"
          class="carousel-view-more-button-card fancy-product-carousel__view-more-button-card product-card__image-link--${imageRatio}${isArch ? ' is-arch' : ''}"
          aria-label="${safeText}"
        >
          <span class="carousel-view-more-button-card__eyebrow">Explore more</span>
          <span class="carousel-view-more-button-card__title">${safeText}</span>
          <span class="carousel-view-more-button-card__icon" aria-hidden="true">→</span>
        </a>
      </article>
    `;

    return item;
  }

  function clearTrack(track) {
    track.querySelectorAll('[data-fancy-product-carousel-item], [data-fancy-product-carousel-view-more]').forEach((item) => {
      item.remove();
    });

    track.querySelectorAll('.featured-collection__item').forEach((item) => {
      if (!item.querySelector('.featured-collection__placeholder-card')) return;
      item.remove();
    });
  }

  function showDynamicEditorNote(section, message) {
    const note = section.querySelector('[data-fancy-product-carousel-dynamic-note]');
    if (!note) return;

    note.textContent = message;
    note.hidden = false;
  }

  function hideDynamicEditorNote(section) {
    const note = section.querySelector('[data-fancy-product-carousel-dynamic-note]');
    if (!note) return;

    note.textContent = '';
    note.hidden = true;
  }

  function hideSection(section) {
    section.hidden = true;
  }

  function showSection(section) {
    section.hidden = false;
  }

  function setLoadingState(section, isLoading) {
    if (!section) return;

    section.classList.toggle('is-loading', Boolean(isLoading));

    if (isLoading) {
      section.setAttribute('aria-busy', 'true');
    } else {
      section.removeAttribute('aria-busy');
    }
  }

  function syncCarouselControls(section) {
    if (typeof window.initCustomCarousels === 'function') {
      window.initCustomCarousels(section);
    }

    window.dispatchEvent(new Event('resize'));
  }

  function getEmptyMessage(config, count) {
    if (config.enableMinProducts && count < Number(config.minProducts || 1)) {
      return `This smart carousel is hidden on the live storefront because it found ${count} product${count === 1 ? '' : 's'}, but the minimum is set to ${config.minProducts}.`;
    }

    return 'This smart carousel is hidden on the live storefront because no matching products were found.';
  }

  function shouldHideForCount(config, count) {
    if (config.enableMinProducts && count < Number(config.minProducts || 1)) return true;
    if (config.hideIfEmpty && count === 0) return true;

    return false;
  }

  async function populateCarousel(section, config) {
    const track = section.querySelector('[data-fancy-product-carousel-track]');
    if (!track) return;

    hideDynamicEditorNote(section);
    setLoadingState(section, true);

    let result;

    try {
      if (config.source === 'recently_viewed') {
        result = await fetchRecentlyViewedItems(config);
      } else {
        result = await fetchFilteredItems(config);
      }
    } catch (error) {
      console.warn('Fancy product carousel: could not fetch products.', error);

      setLoadingState(section, false);

      if (config.editor && config.editor.designMode) {
        showDynamicEditorNote(
          section,
          'This smart carousel could not load matching products. Check that the source filters are available in Shopify Search & Discovery.'
        );
        showSection(section);
      } else {
        hideSection(section);
      }

      return;
    }

    const productsToShow = Number(config.productsToShow || 8);
    const items = result.items || [];
    const shouldHide = shouldHideForCount(config, items.length);

    if (shouldHide) {
      setLoadingState(section, false);

      if (config.editor && config.editor.designMode) {
        clearTrack(track);
        showDynamicEditorNote(section, getEmptyMessage(config, items.length));
        showSection(section);
      } else {
        hideSection(section);
      }

      return;
    }

    clearTrack(track);

    items.slice(0, productsToShow).forEach((resultItem) => {
      track.appendChild(createCarouselItem(resultItem, config));
    });

    if (
      config.source !== 'recently_viewed' &&
      config.viewMore &&
      config.viewMore.enabled &&
      result.hasMoreResults &&
      result.viewMoreUrl
    ) {
      track.appendChild(createViewMoreCard(config, result.viewMoreUrl));
    }

    setLoadingState(section, false);
    showSection(section);
    syncCarouselControls(section);
  }

  function initFancyProductCarousel(section) {
    if (!section || section.dataset.fancyProductCarouselInitialized === 'true') return;

    const config = parseJsonScript(section);
    if (!config) return;

    section.dataset.fancyProductCarouselInitialized = 'true';

    recordCurrentProduct(config);

    if (config.editor && config.editor.hiddenReason) {
      return;
    }

    populateCarousel(section, config);
  }

  function initFancyProductCarousels(container = document) {
    const sections = container.querySelectorAll('[data-fancy-product-carousel]');

    sections.forEach((section) => {
      initFancyProductCarousel(section);
    });
  }

  window.initFancyProductCarousels = initFancyProductCarousels;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initFancyProductCarousels();
    });
  } else {
    initFancyProductCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initFancyProductCarousels(event.target);
  });
})();
